import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addMinutes, formatDate, minutesBetween } from '../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../library/formValidation';
import { DIARY_START_STEP_MINUTES, freeStartTimesOn } from '../../library/freeSlotTimes';
import { EmptySlotResponse, IAppointment } from '../../library/models/appointment';
import { splitApiError } from '../../library/useApiFormError';
import { useIsCompact } from '../../library/useIsCompact';
import ResourcesIds from '../../resources/ResourcesIds';
import { useReactMutation, useReactQuery } from '../../services/queryService';
import { services } from '../../services/services';
import { AppDateTimePicker } from '../pickers/AppDateTimePicker';
import { useSnackbar } from '../layout/snackbarProvider';
import { clockOf } from '../pages/MyCalendar/scheduleEntries';
import { SlotPicker } from '../pages/AppointmentBookingForm/SlotPicker';
import { createRescheduleSchema, RescheduleFormValues } from './rescheduleSchema';
import {
    blockedHintStyles,
    currentWhenStyles,
    fieldStyles,
    keepsLengthStyles,
    requestAlertStyles,
    slotHintStyles,
} from './rescheduleDialog.styles';

export interface IRescheduleDialogProps {
    /** The appointment being moved, or null when the dialog is closed. */
    appointment: IAppointment | null;
    /** Decides which half of this dialog is drawn. Read from the role, never from the row. */
    isStaff: boolean;
    onClose: () => void;
    /** It moved: the host refetches whatever list it drew this from. */
    onMoved: () => void;
}

/**
 * Changing when an appointment happens.
 *
 * **Two ways in, for the same reason booking has two.** A patient picks from
 * {@link SlotPicker} -- windows the API has already said the therapist is free for -- so
 * the answer they give is one the server will accept, and a day the therapist does not
 * work reads *Unavailable* rather than merely empty. Staff get a date and an hour list of
 * their own, still unbounded by anybody's *program* -- an emergency at eight in the evening
 * is a real thing a clinic writes down, and moving one is no different -- but asked for with
 * `includeOutsideProgram`, so it is the whole day minus what already occupies it. That split
 * is not a convenience: it is the same rule the API enforces, drawn. What both halves now
 * share is that neither offers an hour somebody is already in.
 *
 * **Both halves read past this appointment.** `excludeAppointmentId` is what stops the
 * booking occupying its own neighbourhood: without it, shifting a session by a quarter of an
 * hour is refused the window only itself is standing in, and the strip quietly loses the
 * hour the patient already has.
 *
 * **The length is carried, never re-asked.** A move changes the hour and nothing else, so
 * `endsAt` is the old duration added to the new start. Asking again would let the answer
 * disagree with what was sold.
 *
 * **The API's own sentence is what reaches the snackbar.** "This therapist is available on
 * Friday from 09:00 to 17:00" and "that therapist already has something booked then" are
 * the only useful things to say, and a generic failure leaves somebody staring at a slot
 * that visibly has something in it with no idea why. Same reason
 * `AppointmentDetailDialog` reads a refused reopen this way.
 *
 * Who may open it at all is {@code canReschedule}, and the API decides it identically.
 */
export const RescheduleDialog = (props: IRescheduleDialogProps): JSX.Element => {
    const { appointment, isStaff, onClose } = props;
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    /** The patient's half holds its choice whole, exactly as the booking wizard does. */
    const [slot, setSlot] = useState<EmptySlotResponse | null>(null);

    const schema = useMemo(() => createRescheduleSchema(translate), [translate]);

    const {
        control, handleSubmit, reset, formState: { errors, isValid },
    } = useValidatedForm<RescheduleFormValues>(schema, { defaultValues: { startsAt: '' } });

    /** However long it runs for now, which is what it must go on running for. */
    const runsFor: number = appointment
        ? minutesBetween(appointment.startsAt, appointment.endsAt)
        : 0;

    /** The staff half's own day, watched so its hour list follows the date they pick. */
    const typedStartsAt: string = useWatch({ control, name: 'startsAt' }) ?? '';
    const day: string = typedStartsAt.slice(0, 10);

    /**
     * When this therapist is free that day, for as long as the appointment already runs.
     *
     * Only the staff half asks: the patient half is `SlotPicker`, which reaches the same
     * endpoint its own way, over a whole week.
     */
    const { data: slots, isFetching: isFetchingSlots } = useReactQuery<EmptySlotResponse[]>(
        services.appointments.getEmptySlots(day, day, runsFor, appointment?.therapistId ?? 0,
            { includeOutsideProgram: true, startStepMinutes: DIARY_START_STEP_MINUTES,
                excludeAppointmentId: appointment?.id }),
        { enabled: !!appointment && isStaff && !!day && runsFor > 0 }
    );

    const freeTimes: string[] = useMemo(() => freeStartTimesOn(slots, day), [slots, day]);

    // A different appointment is a different question. The dialog is reused rather than
    // remounted, so a slot chosen for one therapist must never be submitted against another.
    useEffect(() => {
        setSlot(null);
        reset({ startsAt: appointment ? appointment.startsAt.slice(0, 16) : '' });
    }, [appointment?.id, appointment?.startsAt, reset]);

    const move = useReactMutation(
        services.appointments.rescheduleMutation(appointment?.id ?? 0)
    );

    const submit = (startsAt: string, endsAt: string): void => {
        move.mutate(
            { startsAt, endsAt },
            {
                // A patient's move is a request again, so it is not "moved" in the sense the
                // staff sentence means -- it is waiting for an answer.
                onSuccess: () => {
                    showSnackbar(translate(isStaff
                        ? ResourcesIds.APPOINTMENT_RESCHEDULED
                        : ResourcesIds.APPOINTMENT_MOVE_REQUESTED), 'success');
                    onClose();
                    props.onMoved();
                },
                onError: (error: unknown) =>
                    showSnackbar(
                        splitApiError(error, [], translate,
                            ResourcesIds.APPOINTMENT_RESCHEDULE_FAILED).formError
                            ?? translate(ResourcesIds.APPOINTMENT_RESCHEDULE_FAILED),
                        'error'
                    ),
            }
        );
    };

    /** The slot's own two ends, sent verbatim -- they are what the API offered. */
    const confirmSlot = (): void => {
        if (slot) {
            submit(slot.startTime, slot.endTime);
        }
    };

    const confirmTyped = handleSubmit((values: RescheduleFormValues): void => {
        const startsAt = `${values.startsAt}:00`;

        submit(startsAt, addMinutes(startsAt, runsFor));
    });

    const canConfirm: boolean = isStaff ? isValid : !!slot;

    return (
        <Dialog
            open={!!appointment}
            onClose={onClose}
            fullWidth
            maxWidth={isStaff ? 'xs' : 'sm'}
            fullScreen={isCompact}
        >
            <DialogTitle>{translate(ResourcesIds.APPOINTMENT_RESCHEDULE_TITLE)}</DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={currentWhenStyles}>
                    {appointment && translate(ResourcesIds.APPOINTMENT_RESCHEDULE_CURRENT, {
                        when: `${formatDate(appointment.startsAt, i18n)} · `
                            + `${clockOf(appointment.startsAt)}–${clockOf(appointment.endsAt)}`,
                    })}
                </Typography>

                {isStaff ? (
                    /* Unbounded by any program on purpose -- see createRescheduleSchema. */
                    <>
                        <Controller
                            name="startsAt"
                            control={control}
                            render={({ field }) => (
                                <AppDateTimePicker
                                    label={translate(ResourcesIds.APPOINTMENT_RESCHEDULE_STARTS_AT)}
                                    timeLabel={translate(ResourcesIds.APPOINTMENT_RESCHEDULE_TIME)}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    inputRef={field.ref}
                                    // No minutesStep: under `strictTime` the list is
                                    // `timeOptions` and nothing is generated from a step, so
                                    // one here would be a number that never applied.
                                    timeOptions={freeTimes}
                                    strictTime
                                    noTimesText={translate(ResourcesIds.MY_CALENDAR_ADD_NO_FREE)}
                                    sx={fieldStyles}
                                    {...fieldProps(errors, 'startsAt', isFetchingSlots
                                        ? translate(ResourcesIds.MY_CALENDAR_ADD_TIMES_LOADING)
                                        : freeTimes.length > 0
                                            ? translate(ResourcesIds.MY_CALENDAR_ADD_FREE_COUNT, {
                                                count: freeTimes.length,
                                            })
                                            : translate(ResourcesIds.MY_CALENDAR_ADD_NO_FREE))}
                                />
                            )}
                        />

                        <Typography variant="caption" color="text.secondary" sx={keepsLengthStyles}>
                            {translate(ResourcesIds.APPOINTMENT_RESCHEDULE_KEEPS_LENGTH, {
                                minutes: runsFor,
                            })}
                        </Typography>
                    </>
                ) : (
                    <>
                        {/* Said before the patient chooses, not after. A move makes it a
                            request again at the new hour, and the hour it had is given up
                            the moment it moves -- so moving something already confirmed
                            risks it, and they have to know that while they can still keep it. */}
                        {appointment?.status === 'SCHEDULED' && (
                            <Alert severity="warning" sx={requestAlertStyles}>
                                {translate(ResourcesIds.APPOINTMENT_RESCHEDULE_CONFIRMED_WARNING, {
                                    name: appointment.therapistName,
                                })}
                            </Alert>
                        )}
                        {appointment?.status === 'REQUESTED' && (
                            <Alert severity="info" sx={requestAlertStyles}>
                                {translate(ResourcesIds.APPOINTMENT_RESCHEDULE_REQUEST_INFO, {
                                    name: appointment.therapistName,
                                })}
                            </Alert>
                        )}

                        <Typography variant="body2" sx={slotHintStyles}>
                            {translate(ResourcesIds.APPOINTMENT_RESCHEDULE_PICK_SLOT)}
                        </Typography>

                        {/* The window is this appointment's own length, so what is offered
                            is somewhere it actually fits -- and the therapist is the one it
                            already has, because a patient never hands it to a colleague. */}
                        {appointment && (
                            <SlotPicker
                                therapistId={appointment.therapistId}
                                durationMinutes={runsFor}
                                excludeAppointmentId={appointment.id}
                                selected={slot}
                                onSelect={setSlot}
                            />
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                {/* Never a greyed button with no explanation beside it. */}
                {!canConfirm && (
                    <Typography variant="caption" color="text.secondary" sx={blockedHintStyles}>
                        {translate(isStaff
                            ? ResourcesIds.FORM_BLOCKED_SAVE
                            : ResourcesIds.APPOINTMENT_RESCHEDULE_PICK_SLOT)}
                    </Typography>
                )}

                <Button onClick={onClose}>{translate(ResourcesIds.CLOSE_DIALOG)}</Button>
                <Button
                    variant="contained"
                    disabled={!canConfirm || move.isPending}
                    onClick={isStaff ? confirmTyped : confirmSlot}
                >
                    {translate(ResourcesIds.APPOINTMENT_RESCHEDULE_CONFIRM)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
